import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { fetchActivityAlerts, isActivityAlertType } from '@apis/activityAlerts';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatYmd } from '@utils/dateUtils';
import { activityItemKey, issueItemKey, type ListItemKind } from '../model/listItem';

const PARAM_TYPE = 'alertType';
const PARAM_DAY = 'alertDay';

/**
 * Narrows the list to the members of one alert group, addressed by the
 * `?alertType=…&alertDay=…` descriptor the dashboard card links with.
 *
 * The descriptor is resolved here rather than passed as a list of ids so the
 * URL stays short and shareable, and so the set reflects the alerts as they are
 * when the link is opened rather than when the card rendered it.
 */
export function useAlertFilter(siteId: string | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawType = searchParams.get(PARAM_TYPE);
  const type = isActivityAlertType(rawType) ? rawType : null;
  const day = searchParams.get(PARAM_DAY);
  // The kind of row the alert is about — issue alerts are the `issue-*` types.
  const kind: ListItemKind = type?.startsWith('issue-') ? 'issue' : 'activity';
  const isRequested = Boolean(siteId && type && day);

  const { data, isPending } = useQuery({
    queryKey: ['activity-alerts', siteId],
    queryFn: ({ signal }) => fetchActivityAlerts(siteId!, signal),
    enabled: isRequested,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Same tz source as the card that minted the deep link (the site's own
  // zone, matching the sweep's local-midnight day), so `alertDay` resolves to
  // the identical member set on both ends of the link.
  const siteTz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;

  const memberKeys = useMemo<ReadonlySet<string> | null>(() => {
    if (!isRequested || !data) return null;
    const keys = new Set<string>();
    for (const alert of data.alerts) {
      if (alert.type !== type) continue;
      if (formatYmd(alert.firedAt, siteTz) !== day) continue;
      keys.add(alert.issueId ? issueItemKey(alert.issueId) : activityItemKey(alert.taskId));
    }
    return keys;
  }, [isRequested, data, type, day, siteTz]);

  const clear = useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(PARAM_TYPE);
        next.delete(PARAM_DAY);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return {
    isActive: isRequested,
    type,
    day,
    kind,
    memberKeys,
    /** The descriptor is on and its alerts are still loading — the members are
     *  not known yet, so a list must not paint "nothing matches". */
    resolving: isRequested && isPending,
    clear,
  };
}
